import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [isPlan, setIsPlan] = useState(false);

  useEffect(() => {
    if (!sessionId) { setStatus("unknown"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          setIsPlan(!res.data.job_id);
          return;
        }
      } catch { /* status poll is best-effort */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <main className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      <Seo title="Payment | HiredToday" description="Payment confirmation." path="/payment/success" />
      {status === "checking" && (
        <>
          <div className="h-10 w-10 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950 mt-6">Confirming your payment…</h1>
          <p className="mt-3 text-zinc-600">This takes a few seconds. Don't close the page.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950 mt-6" data-testid="payment-confirmed">
            {isPlan ? "Your plan is active." : "Paid. Your job is in the review queue."}
          </h1>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            {isPlan
              ? "This month's listing credits are ready in your dashboard. They renew every month while your plan is active."
              : "It's usually live within one working day. Track it any time from your dashboard."}
          </p>
          <Link to="/dashboard" data-testid="payment-success-dashboard"
            className="mt-8 inline-block bg-zinc-950 text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-zinc-800 transition-colors duration-200">
            Go to my dashboard
          </Link>
        </>
      )}
      {(status === "timeout" || status === "unknown") && (
        <>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">We're still checking.</h1>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            If your payment went through, your job will show as "in review" in your dashboard shortly. If it's not there in ten minutes, contact us and we'll sort it.
          </p>
          <Link to="/dashboard" data-testid="payment-pending-dashboard"
            className="mt-8 inline-block bg-zinc-950 text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-zinc-800 transition-colors duration-200">
            Check my dashboard
          </Link>
        </>
      )}
    </main>
  );
}
